import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import { useTheme } from '../contexts/ThemeContext';
import logo from '../assets/logos/logo.png';
import {
  ShoppingBag, Truck, UtensilsCrossed, ClipboardList,
  LogOut, ChevronDown, Store, FileText,
  PhoneIncoming, Users, X, RefreshCw, Trash2, Sun, Moon,
  BookOpen, LayoutGrid, Calculator, Star, ChefHat
} from 'lucide-react';
import FiscalDocumentsViewer from './shared/FiscalDocumentsViewer';
import RegisterHubModal from './register/RegisterHubModal';
import { registerAccess } from './register/registerAccess';
import WasteModal from './shared/WasteModal';
import DeliveryNoteModal, { DeliveryNoteIssued } from './shared/DeliveryNoteModal';
import { useToast } from './shared/Toast';
import DailyDishesModal from './dailyDishes/DailyDishesModal';
import { usePermissions } from '../hooks/usePermissions';
import MenuMoreModal, { MenuEntry, MenuEntryIcon, trailingClass } from './shared/MenuMoreModal';
import CallHistory from './CallHistory';
import CustomerManagement from './CustomerManagement';
import CatalogChangesModal from './CatalogChangesModal';
import SupportContact from './SupportContact';
import { organizationAPI, meAPI } from '../services/api';
import { useMenuPins } from '../hooks/useMenuPins';
import { useManualInfo } from '../hooks/useManualInfo';
import { useDeliveryNoteAvailable } from '../hooks/useDeliveryNoteAvailable';

// Μενού χρήστη (πάνω δεξιά): μέχρι τόσες επιλογές φαίνονται όλες. Πάνω από
// αυτό φαίνονται μόνο όσες έχει διαλέξει ο χρήστης + «Περισσότερα».
const MENU_COLLAPSE_AT = 6;
// Προεπιλογή όταν ο χρήστης δεν έχει διαλέξει: οι επιλογές της καθημερινής
// βάρδιας. Όσες δεν ισχύουν στο κατάστημα απλώς αγνοούνται (το Δελτίο Αποστολής
// φαίνεται μόνο όπου το άνοιξε το κατάστημα, άρα όπου υπάρχει το θέλουν πρόχειρο).
const DEFAULT_MENU_PINS = ['catalogChanges', 'register', 'dailyDishes', 'favorites', 'fiscalDocuments', 'waste', 'deliveryNote'];
// Οι τέσσερις παλιές επιλογές που έγιναν το «Ταμείο» (6/10/2026). Καρφιτσώματα που έχουν ήδη
// αποθηκευτεί με αυτές (server + localStorage) μετράνε ως «Ταμείο».
const LEGACY_REGISTER_PINS = ['staffPayments', 'xReport', 'treatsReport', 'closeRegister'];
const normalizeMenuPins = (pins: string[]): string[] =>
  Array.from(new Set(pins.map(id => (LEGACY_REGISTER_PINS.includes(id) ? 'register' : id))));

const readManualSeen = (key: string | null): number | null => {
  try { return key ? Number(localStorage.getItem(key)) || null : null; } catch { return null; }
};

interface NavigationProps {
  currentPage: 'dashboard' | 'orders' | 'takeaway' | 'delivery' | 'dinein';
  onPageChange: (page: string) => void;
  /** «Αγαπημένα» στο μενού — το δίνει μόνο το ταμείο take away, όταν είναι ανοιχτός ο διακόπτης. */
  onOpenFavorites?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ currentPage, onPageChange, onOpenFavorites }) => {
  const { store, employee, employeeLogout } = useAuth();
  const { resolved: themeMode, toggle: toggleTheme, look } = useTheme();
  // Νέα εμφάνιση: λευκή μπάρα, καρτέλες αριστερά, ΕΝΑ χρώμα για την ενεργή (όχι χρώμα ανά καρτέλα).
  const v2 = look === 'v2';
  const { t } = useTranslation(['common', 'orders', 'delivery', 'dineIn', 'takeaway', 'analytics', 'dailyDishes']);
  const { hasPermission } = usePermissions();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showRegisterHub, setShowRegisterHub] = useState(false);
  const [showWaste, setShowWaste] = useState(false);
  const [showDailyDishes, setShowDailyDishes] = useState(false);
  const [showDeliveryNote, setShowDeliveryNote] = useState(false);
  const [showFiscalDocuments, setShowFiscalDocuments] = useState(false);
  const [showCallHistory, setShowCallHistory] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showCatalogChanges, setShowCatalogChanges] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualError, setManualError] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Επιλογές μενού ανά υπάλληλο (ο ρόλος στο κλειδί ξεχωρίζει τους χρήστες
  // κεντρικών, των οποίων το id είναι από άλλον πίνακα).
  const menuIdentity = store?.id ? `${store.id}:${employee?.role || 'store'}:${employee?.id ?? 0}` : null;
  const { pins: menuPins, setPins: setMenuPins } = useMenuPins(menuIdentity);
  const { manual, refresh: refreshManual } = useManualInfo(store?.id, !!store?.organization_id);
  useEffect(() => { if (showUserMenu) refreshManual(); }, [showUserMenu, refreshManual]);
  const { available: deliveryNoteAvailable, refresh: refreshDeliveryNote } = useDeliveryNoteAvailable(store?.id);
  useEffect(() => { if (showUserMenu) refreshDeliveryNote(); }, [showUserMenu, refreshDeliveryNote]);
  const { showToast } = useToast();
  // Ποια έκδοση εγχειριδίου κατέβασε ο υπάλληλος σε αυτή τη συσκευή: μέχρι να
  // κατεβάσει την τελευταία, η επιλογή εμφανίζεται στο μενού με «Νέα έκδοση»
  // ακόμα κι αν δεν την έχει καρφιτσώσει.
  const manualSeenKey = store?.id ? `xf_manual_seen:${store.id}:${employee?.role || 'store'}:${employee?.id ?? 0}` : null;
  const [manualSeenId, setManualSeenId] = useState<number | null>(() => readManualSeen(manualSeenKey));
  useEffect(() => { setManualSeenId(readManualSeen(manualSeenKey)); }, [manualSeenKey]);
  const manualIsNew = !!manual && manualSeenId !== manual.id;

  // Ένδειξη σύνδεσης: ΠΡΑΓΜΑΤΙΚΗ — η σύνδεση με τον server (από εκεί έρχονται οι
  // παραγγελίες) ΚΑΙ το internet της συσκευής. Πριν ήταν στατική πράσινη
  // κουκκίδα που δεν έλεγχε τίποτα, ενώ το εγχειρίδιο έλεγε να την κοιτάς κάθε
  // πρωί. «Εκτός σύνδεσης» μόνο αν κρατήσει >5″, ώστε το αρχικό connect και οι
  // σύντομες επανασυνδέσεις να μην αναβοσβήνουν κόκκινο.
  const { isConnected: socketConnected } = useSocket();
  // Χωρίς κατάστημα (π.χ. προβολή κεντρικών) δεν ανοίγει socket — μην το δείξεις ως βλάβη.
  const socketUp = socketConnected || !store?.id;
  const [netUp, setNetUp] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setNetUp(true);
    const off = () => setNetUp(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  const [showOffline, setShowOffline] = useState(false);
  useEffect(() => {
    if (socketUp && netUp) { setShowOffline(false); return; }
    const timer = setTimeout(() => setShowOffline(true), 5000);
    return () => clearTimeout(timer);
  }, [socketUp, netUp]);

  // Poll pending catalog changes for org stores so cashiers see the dot without opening the menu.
  const refreshPendingCount = React.useCallback(async () => {
    if (!store?.organization_id || !store?.id) { setPendingSyncCount(0); return; }
    try {
      const d = await organizationAPI.getPendingSyncRequests(store.id);
      setPendingSyncCount(d.count ?? (d.requests?.length || 0));
    } catch { /* ignore */ }
  }, [store?.id, store?.organization_id]);

  useEffect(() => {
    if (!store?.organization_id || !store?.id) { setPendingSyncCount(0); return; }
    refreshPendingCount();
    const iv = setInterval(refreshPendingCount, 60000);
    return () => clearInterval(iv);
  }, [store?.id, store?.organization_id, refreshPendingCount]);

  // Close user menu on outside click/touch
  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [showUserMenu]);

  const allNavigationItems = [
    {
      id: 'takeaway',
      label: 'Take Away',
      shortLabel: 'T/Away',
      showIf: store?.takeaway !== false,
      icon: ShoppingBag,
      color: 'blue'
    },
    {
      id: 'delivery',
      label: 'Delivery',
      shortLabel: 'Delivery',
      showIf: store?.delivery !== false,
      icon: Truck,
      color: 'emerald'
    },
    {
      id: 'dinein',
      label: 'Dine-In',
      shortLabel: 'Dine-In',
      showIf: store?.dine_in === true,
      icon: UtensilsCrossed,
      color: 'amber'
    },
    {
      id: 'orders',
      label: t('orders:title'),
      shortLabel: t('orders:title'),
      showIf: employee?.role === 'store_admin' || employee?.role === 'super_admin' || employee?.permissions?.view_orders_tab !== false,
      icon: ClipboardList,
      color: 'violet'
    }
  ];

  const navigationItems = allNavigationItems.filter(item => item.showIf !== false);

  // Auto-redirect if current page is not in visible tabs
  useEffect(() => {
    if (navigationItems.length > 0 && !navigationItems.some(item => item.id === currentPage)) {
      onPageChange(navigationItems[0].id);
    }
  }, [navigationItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    setShowUserMenu(false);
    employeeLogout();
  };

  const handleNavClick = (pageId: string) => {
    if (pageId !== currentPage) {
      onPageChange(pageId);
    }
  };

  // Active tab color classes
  const getActiveClasses = (color: string) => {
    const map: Record<string, string> = {
      blue: 'bg-blue-600 text-white shadow-lg shadow-blue-600/25',
      emerald: 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/25',
      amber: 'bg-amber-600 text-white shadow-lg shadow-amber-600/25',
      violet: 'bg-violet-600 text-white shadow-lg shadow-violet-600/25',
    };
    return map[color] || map.blue;
  };

  const getInactiveIconColor = (color: string) => {
    const map: Record<string, string> = {
      blue: 'text-blue-600 dark:text-blue-400',
      emerald: 'text-emerald-600 dark:text-emerald-400',
      amber: 'text-amber-700 dark:text-amber-400',
      violet: 'text-violet-600 dark:text-violet-400',
    };
    return map[color] || map.blue;
  };

  // Employee initials for avatar
  const getInitials = () => {
    if (employee?.first_name && employee?.last_name) {
      return `${employee.first_name.charAt(0)}${employee.last_name.charAt(0)}`;
    }
    if (employee?.first_name) return employee.first_name.charAt(0);
    if (employee?.name) return employee.name.charAt(0);
    return store?.store_name?.charAt(0) || 'X';
  };

  const getDisplayName = () => {
    if (employee?.first_name && employee?.last_name) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    return employee?.name || employee?.first_name || store?.store_name || 'Staff';
  };

  const isStoreAdmin = employee?.role === 'store_admin' || employee?.role === 'super_admin' || employee?.role === 'admin';

  const closeMenus = () => { setShowUserMenu(false); setShowMore(false); };

  const downloadManual = async () => {
    if (!manual || manualBusy) return;
    setManualBusy(true);
    setManualError(false);
    try {
      await meAPI.downloadManual(manual.file_name);
      setManualSeenId(manual.id);
      try { if (manualSeenKey) localStorage.setItem(manualSeenKey, String(manual.id)); } catch { /* χωρίς αποθήκευση */ }
      closeMenus();
    } catch {
      setManualError(true);
    } finally {
      setManualBusy(false);
    }
  };

  // Όλες οι επιλογές του μενού, με τη σειρά που εμφανίζονται. Οι συνθήκες
  // ορατότητας (ρόλος/δικαιώματα/ρυθμίσεις καταστήματος) μένουν όπως ήταν.
  const menuEntries: MenuEntry[] = [];
  // Αλλαγές καταλόγου — κάθε χρήστης καταστήματος οργανισμού (και ταμίες)
  if (store?.organization_id) {
    menuEntries.push({
      id: 'catalogChanges', label: 'Αλλαγές καταλόγου', icon: RefreshCw, tone: 'blue',
      badge: pendingSyncCount,
      trailing: pendingSyncCount > 0 ? `${pendingSyncCount} νέες` : undefined,
      alert: pendingSyncCount > 0,
      onSelect: () => setShowCatalogChanges(true),
    });
  }
  // «Ταμείο» — ένα παράθυρο στη θέση των «Πληρωμές Προσωπικού», «Χ Αναφορά», «Κεράσματα» και
  // «Κλείσιμο Ταμείου» (απόφαση 6/10/2026). Εμφανίζεται όταν ο χρήστης έχει έστω μία από αυτές· οι
  // κανόνες είναι οι ίδιοι με πριν (register/registerAccess).
  if (registerAccess(store, employee).any) {
    menuEntries.push({
      id: 'register', label: t('common:registerHub', { defaultValue: 'Ταμείο' }), icon: Calculator, tone: 'blue',
      onSelect: () => setShowRegisterHub(true),
    });
  }
  // Πιάτα ημέρας (migration 278): ποια πουλιούνται σήμερα, τιμή, νέο πιάτο, «Καθαρισμός ημέρας». Μόνο με
  // ανοιχτή τη λειτουργία του καταστήματος, για τον διαχειριστή ή όποιον έχει το δικαίωμα daily_dishes_manage.
  if (store?.daily_dishes_enabled === true && (isStoreAdmin || hasPermission('daily_dishes_manage'))) {
    menuEntries.push({
      id: 'dailyDishes', label: t('dailyDishes:menu.entry'), icon: ChefHat, tone: 'emerald',
      onSelect: () => setShowDailyDishes(true),
    });
  }
  // Αγαπημένα — συντομεύσεις του καταλόγου take away (Ρυθμίσεις → Take away)
  if (onOpenFavorites) {
    menuEntries.push({ id: 'favorites', label: 'Αγαπημένα', icon: Star, tone: 'amber', onSelect: onOpenFavorites });
  }
  // Φύρα — μόνο όταν το κατάστημα έχει ενεργή την καταγραφή
  if (store?.waste_enabled === true) {
    menuEntries.push({ id: 'waste', label: 'Φύρα', icon: Trash2, tone: 'amber', onSelect: () => setShowWaste(true) });
  }
  // Δελτίο Αποστολής 9.3 — ό,τι φεύγει από το κατάστημα χωρίς πώληση (πρώτες ύλες σε προμηθευτή, συσκευή για
  // service): ο ταμίας γράφει τις γραμμές. Μόνο με ανοιχτό διακόπτη (Ρυθμίσεις → Take Away) και ECR.
  if (deliveryNoteAvailable) {
    menuEntries.push({
      id: 'deliveryNote', label: t('takeaway:deliveryNote.button'), icon: Truck, tone: 'cyan',
      onSelect: () => setShowDeliveryNote(true),
    });
  }
  if (isStoreAdmin || employee?.permissions?.view_fiscal_documents === true) {
    menuEntries.push({ id: 'fiscalDocuments', label: t('orders:fiscal.document'), icon: FileText, tone: 'cyan', onSelect: () => setShowFiscalDocuments(true) });
  }
  if (isStoreAdmin || employee?.permissions?.view_call_history === true) {
    menuEntries.push({ id: 'callHistory', label: t('delivery:callHistoryTitle', { defaultValue: 'Ιστορικό Κλήσεων' }), icon: PhoneIncoming, tone: 'emerald', onSelect: () => setShowCallHistory(true) });
  }
  if (isStoreAdmin || employee?.permissions?.view_customers === true) {
    menuEntries.push({ id: 'customers', label: t('common:customers', { defaultValue: 'Πελάτες' }), icon: Users, tone: 'violet', onSelect: () => setShowCustomers(true) });
  }
  // Εγχειρίδιο — μόνο αν τα κεντρικά έχουν ανεβάσει PDF. Η λήψη φέρνει πάντα
  // την τελευταία έκδοση· το μενού μένει ανοιχτό μέχρι να ολοκληρωθεί.
  if (manual) {
    menuEntries.push({
      id: 'manual', label: 'Εγχειρίδιο χρήσης', icon: BookOpen, tone: 'indigo',
      keepOpen: true, busy: manualBusy,
      alert: manualIsNew,
      trailing: manualIsNew ? 'Νέα έκδοση' : undefined,
      trailingTone: 'info',
      hint: manualBusy ? 'Λήψη…'
        : manualError ? 'Η λήψη απέτυχε — πάτα ξανά'
        : `Έκδοση ${new Date(manual.uploaded_at).toLocaleDateString('el-GR')}`,
      hintTone: manualError && !manualBusy ? 'error' : 'muted',
      onSelect: downloadManual,
    });
  }
  // Εναλλαγή θέματος — ανά συσκευή, όχι ανά χρήστη: το ίδιο ταμείο το
  // χειρίζονται πολλοί υπάλληλοι, και το θέμα εξαρτάται από την οθόνη και τον
  // φωτισμό του καταστήματος.
  menuEntries.push({
    id: 'theme', label: themeMode === 'dark' ? 'Ανοιχτό θέμα' : 'Σκούρο θέμα',
    icon: themeMode === 'dark' ? Sun : Moon, tone: 'amber', onSelect: toggleTheme,
  });

  const menuCollapsed = menuEntries.length > MENU_COLLAPSE_AT;
  const currentPins = normalizeMenuPins(menuPins ?? DEFAULT_MENU_PINS);
  const pinnedIds = new Set(currentPins);
  const visibleEntries = menuCollapsed ? menuEntries.filter(e => pinnedIds.has(e.id) || e.alert) : menuEntries;
  const hiddenCount = menuEntries.length - visibleEntries.length;

  const runEntry = (entry: MenuEntry) => {
    if (!entry.keepOpen) closeMenus();
    entry.onSelect();
  };

  // Αποθηκεύεται ήδη με το «register» στη θέση των παλιών επιλογών.
  const toggleMenuPin = (id: string) => {
    setMenuPins(currentPins.includes(id) ? currentPins.filter(p => p !== id) : [...currentPins, id]);
  };

  return (
    <>
      <nav className={v2
        ? 'xv2-nav bg-surface-2 border-b border-line sticky top-0 z-50'
        : 'cashier-navigation bg-surface-0/95 backdrop-blur-md border-b border-line/60 sticky top-0 z-50'}>
        <div className="w-full px-3 sm:px-5">
          <div className={`flex items-center justify-between ${v2 ? 'h-14' : 'h-[60px]'}`}>

            {/* Left: Logo */}
            <div className="flex items-center gap-3 shrink-0">
              <img
                src={logo}
                alt="Logo"
                className={v2 ? 'w-8 h-8 rounded-lg' : 'w-9 h-9 rounded-lg shadow-md'}
              />
              <div className="hidden lg:block">
                <h1 className="text-base font-bold text-t1 tracking-tight leading-none">Xentrix Flow</h1>
                <p className={v2
                  ? 'text-[11px] text-t3 font-medium leading-tight mt-0.5'
                  : 'text-[10px] text-t3 dark:text-t4 font-medium uppercase tracking-widest'}>{store?.store_name || 'POS'}</p>
              </div>
            </div>

            {/* Νέα εμφάνιση: καρτέλες αριστερά, η ενεργή με απαλό μπλε και γραμμή στο κάτω όριο */}
            {v2 && navigationItems.length > 1 && (
            <div className="flex items-center flex-1 gap-1 mx-2 sm:mx-6 min-w-0">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPage === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item.id)}
                    aria-current={isActive ? 'page' : undefined}
                    className={`relative flex items-center gap-2 px-3 sm:px-3.5 h-10 rounded-lg text-sm font-medium transition-colors select-none touch-manipulation active:scale-[0.97] ${
                      isActive
                        ? 'bg-brand-soft text-brand after:absolute after:left-2 after:right-2 after:-bottom-[9px] after:h-0.5 after:rounded-full after:bg-brand'
                        : 'text-t2 hover:bg-surface-3 hover:text-t1'
                    }`}
                  >
                    <Icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? 'text-brand' : 'text-t3'}`} />
                    <span className="hidden sm:inline whitespace-nowrap">{item.label}</span>
                    <span className="sm:hidden text-xs whitespace-nowrap">{item.shortLabel}</span>
                  </button>
                );
              })}
            </div>
            )}

            {/* Center: Navigation Tabs - hidden when only 1 tab */}
            {!v2 && navigationItems.length > 1 && (
            <div className="flex items-center justify-center flex-1 mx-2 sm:mx-4">
              <div className="cashier-navigation-tabs flex items-center bg-surface-1/70 rounded-2xl p-1 gap-1 border border-line/40">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPage === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item.id)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`
                        cashier-navigation-tab relative flex items-center gap-2 sm:gap-2.5 px-3 sm:px-5 py-2.5 sm:py-3
                        rounded-xl text-sm font-semibold
                        transition-all duration-200 ease-out
                        select-none touch-manipulation
                        active:scale-[0.96]
                        ${isActive 
                          ? getActiveClasses(item.color) 
                          : 'text-t3 hover:text-t2 hover:bg-surface-3 dark:hover:bg-surface-2/50'
                        }
                      `}
                    >
                      <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'light:text-white dark:text-t1' : getInactiveIconColor(item.color)}`} />
                      <span className="hidden sm:inline whitespace-nowrap">{item.label}</span>
                      <span className="sm:hidden text-xs whitespace-nowrap">{item.shortLabel}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            )}

            {/* Right: User Menu */}
            <div className="flex items-center gap-2 shrink-0" ref={userMenuRef}>
              {/* Τηλέφωνο υποστήριξης — πάντα ορατό στο POS (tap-to-call) */}
              <SupportContact variant="mini" />
              {/* Ένδειξη σύνδεσης — πράσινη διακριτική· κόκκινη με κείμενο (και σε κινητό) όταν χαθεί */}
              {showOffline ? (
                <div
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-red-100 dark:bg-red-950/50 border border-red-300 dark:border-red-800/60"
                  title="Χωρίς σύνδεση με τον server — οι νέες παραγγελίες δεν έρχονται. Έλεγξε το internet."
                  role="status"
                >
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-xs font-semibold text-red-700 dark:text-red-300 whitespace-nowrap">Εκτός σύνδεσης</span>
                </div>
              ) : (
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse hidden sm:block" title="Συνδεδεμένο" />
              )}

              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={`
                  relative flex items-center gap-2 p-1.5 sm:p-2 rounded-xl
                  transition-all duration-200 touch-manipulation select-none
                  ${showUserMenu
                    ? 'bg-surface-2 ring-1 ring-line-strong'
                    : 'hover:bg-surface-1 active:bg-surface-2'
                  }
                `}
              >
                <div className={v2
                  ? 'relative w-9 h-9 bg-t1 rounded-full flex items-center justify-center'
                  : 'cashier-avatar relative w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-md'}>
                  <span className={v2 ? 'text-surface-2 font-semibold text-sm' : 'light:text-white font-bold text-sm dark:text-t1'}>{getInitials()}</span>
                  {/* Pending catalog-changes indicator (visible while the menu is closed) */}
                  {store?.organization_id && pendingSyncCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold ring-2 ring-surface-0 animate-pulse">
                      {pendingSyncCount > 99 ? '99+' : pendingSyncCount}
                    </span>
                  )}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-t2 leading-tight">
                    {employee?.first_name || employee?.name || 'Staff'}
                  </p>
                  <p className="text-[11px] text-t3 dark:text-t4 leading-tight">{employee?.role_display || 'Staff'}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-t4 hidden md:block transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>

              {/* User Dropdown */}
              {showUserMenu && (
                <div className="absolute right-3 top-[calc(100%+4px)] w-72 bg-surface-2 dark:bg-surface-1 rounded-2xl shadow-2xl shadow-black/15 dark:shadow-black/50 border border-line/60 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                  {/* User Info Header */}
                  <div className="px-5 py-4 bg-surface-0 border-b border-line/60">
                    <div className="flex items-center gap-3">
                      <div className={v2
                        ? 'w-11 h-11 bg-t1 rounded-full flex items-center justify-center'
                        : 'cashier-avatar w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg'}>
                        <span className={v2 ? 'text-surface-2 font-semibold text-base' : 'light:text-white font-bold text-base dark:text-t1'}>{getInitials()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-t1 truncate">{getDisplayName()}</p>
                        <p className="text-sm text-t3">{employee?.role_display || 'Staff'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3 px-1">
                      <Store className="w-3.5 h-3.5 text-t4" />
                      <p className="text-xs text-t3 dark:text-t4 font-medium">{store?.store_name}</p>
                      <div className="flex items-center gap-1 ml-auto">
                        <div className={`w-1.5 h-1.5 rounded-full ${showOffline ? 'bg-red-500' : 'bg-green-400'}`} />
                        <span className={`text-xs font-medium ${showOffline ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          {showOffline ? 'Εκτός σύνδεσης' : 'Online'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items — οι καρφιτσωμένες (ή όλες, αν είναι λίγες) + «Περισσότερα» */}
                  <div className="py-1.5 max-h-[calc(100vh-190px)] overflow-y-auto">
                    {visibleEntries.map(entry => (
                      <button
                        key={entry.id}
                        onClick={() => runEntry(entry)}
                        disabled={entry.busy}
                        className="w-full text-left px-5 py-3.5 text-base text-t2 hover:bg-surface-3 dark:hover:bg-surface-2/60 active:bg-surface-3 dark:active:bg-surface-3/60 flex items-center gap-3.5 transition-colors touch-manipulation disabled:opacity-70"
                      >
                        <MenuEntryIcon entry={entry} />
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium truncate">{entry.label}</span>
                          {entry.hint && (
                            <span className={`block text-xs truncate ${entry.hintTone === 'error' ? 'text-red-600 dark:text-red-400' : 'text-t3'}`}>
                              {entry.hint}
                            </span>
                          )}
                        </span>
                        {entry.trailing && (
                          <span className={`text-xs font-semibold shrink-0 ${trailingClass(entry)}`}>{entry.trailing}</span>
                        )}
                      </button>
                    ))}

                    {menuCollapsed && (
                      <button
                        onClick={() => { setShowMore(true); setShowUserMenu(false); }}
                        className="w-full text-left px-5 py-3.5 text-base text-t2 hover:bg-surface-3 dark:hover:bg-surface-2/60 active:bg-surface-3 dark:active:bg-surface-3/60 flex items-center gap-3.5 transition-colors touch-manipulation"
                      >
                        <div className="w-9 h-9 bg-surface-3 rounded-xl flex items-center justify-center">
                          <LayoutGrid className="w-5 h-5 text-t2" />
                        </div>
                        <span className="flex-1 font-medium">Περισσότερα</span>
                        {hiddenCount > 0 && (
                          <span className="text-xs font-semibold text-t3 bg-surface-3 rounded-full px-2 py-0.5">+{hiddenCount}</span>
                        )}
                      </button>
                    )}

                    {/* Switch User / Logout */}
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-5 py-3.5 text-base text-t2 hover:bg-red-100 dark:hover:bg-red-900/20 active:bg-red-100 dark:active:bg-red-900/30 flex items-center gap-3.5 transition-colors touch-manipulation group"
                    >
                      <div className="w-9 h-9 bg-red-600/10 group-hover:bg-red-600/20 rounded-xl flex items-center justify-center transition-colors">
                        <LogOut className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <span className="font-medium group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors">{t('auth:logout', { ns: 'auth' })}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* «Ταμείο»: Σύνοψη (Χ) · Προσωπικό · Κεράσματα & ακυρώσεις + Επανεκτύπωση Ζ / Εκτύπωση Χ / Κλείσιμο */}
      <RegisterHubModal
        isOpen={showRegisterHub}
        onClose={() => setShowRegisterHub(false)}
      />

      {/* Πιάτα ημέρας: «Σήμερα» από το ταμείο (ίδια οθόνη με τη διαχείριση) */}
      {showDailyDishes && store?.id && (
        <DailyDishesModal storeId={store.id} onClose={() => setShowDailyDishes(false)} />
      )}

      {/* Waste recording */}
      <WasteModal
        isOpen={showWaste}
        onClose={() => setShowWaste(false)}
      />

      {/* Δελτίο Αποστολής: εκδίδεται και τυπώνεται από τον server */}
      {showDeliveryNote && store?.id && (
        <DeliveryNoteModal
          storeId={store.id}
          onClose={() => setShowDeliveryNote(false)}
          onIssued={({ pending }: DeliveryNoteIssued) => {
            setShowDeliveryNote(false);
            if (pending) showToast('warning', t('takeaway:deliveryNote.issuedPending'));
            else showToast('success', t('takeaway:deliveryNote.issued'));
          }}
        />
      )}

      {/* «Περισσότερα»: όλες οι επιλογές + ποιες φαίνονται κατευθείαν στο μενού */}
      <MenuMoreModal
        isOpen={showMore}
        onClose={() => setShowMore(false)}
        entries={menuEntries}
        pinned={pinnedIds}
        onTogglePin={toggleMenuPin}
        onResetPins={() => setMenuPins(null)}
        customized={menuPins !== null}
        onSelect={runEntry}
      />

      {/* Fiscal Documents Viewer */}
      <FiscalDocumentsViewer
        isOpen={showFiscalDocuments}
        onClose={() => setShowFiscalDocuments(false)}
      />

      <CatalogChangesModal
        store={store}
        isOpen={showCatalogChanges}
        onClose={() => setShowCatalogChanges(false)}
        onChanged={refreshPendingCount}
      />

      {/* Call History Modal */}
      {showCallHistory && (
        <div
          className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowCallHistory(false)}
        >
          <div
            className="cashier-dialog bg-surface-0 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cashier-dialog-header flex items-center justify-between gap-3 px-5 py-4 border-b border-line bg-surface-1 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-600/20 flex items-center justify-center">
                  <PhoneIncoming className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="text-lg font-bold text-t1">{t('delivery:callHistoryTitle', { defaultValue: 'Ιστορικό Κλήσεων' })}</h2>
              </div>
              <button
                onClick={() => setShowCallHistory(false)}
                className="w-9 h-9 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all"
                title={t('common:close', { defaultValue: 'Close' })}
              >
                <X className="h-5 w-5 text-t2" />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <CallHistory embedded />
            </div>
          </div>
        </div>
      )}

      {/* Customers Modal */}
      {showCustomers && (
        <div
          className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowCustomers(false)}
        >
          <div
            className="cashier-dialog bg-surface-0 rounded-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cashier-dialog-header flex items-center justify-between gap-3 px-5 py-4 border-b border-line bg-surface-1 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-600/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <h2 className="text-lg font-bold text-t1">{t('common:customers', { defaultValue: 'Πελάτες' })}</h2>
              </div>
              <button
                onClick={() => setShowCustomers(false)}
                className="w-9 h-9 rounded-xl bg-surface-2 hover:bg-surface-3 flex items-center justify-center active:scale-95 transition-all"
                title={t('common:close', { defaultValue: 'Close' })}
              >
                <X className="h-5 w-5 text-t2" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <CustomerManagement embedded />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
